/* eslint-disable complexity */
import { Box, Button, Stack } from '@mui/material';
import React, { FC, useMemo, useState } from 'react';

import { AbgTypography, paletteBase } from '@abg/kit-base';
import { OpeningHours } from '@abg/typings';
import { usePageContext } from '@abg/web-core/context/page/client-provider';

import { ConfigItemFormContainerMobile } from '../../../../organisms/BookingWidget/molecules/config-item/ConfigItemFormContainerMobile';
import { formatHours } from '../../../../organisms/LocationMap/utils/operation-hours.util';
import { formatPageLocaleToISOCode } from '../../../../shared/utils/format-page-locale';
import { AbgDivider } from '../../../AbgDivider';
import { EmptyState } from '../../atoms/EmptyState';
import { LocationIcon } from '../../atoms/location-icon/LocationIcon';
import { LocationFeatures } from '../location-features/LocationFeatures';

import { LocationDetailsMobileProperties } from './location-details.types';
import * as styles from './LocationDetailsMobile.styling';
import { LocationHolidayHours } from './LocationHolidayHours';
import { getLocationDetailsAddress } from './utils';

export const LocationDetailsMobile: FC<LocationDetailsMobileProperties> = ({
  isOpen,
  handleClose,
  option,
  uiTextData,
  onSelectLocation,
  locationType,
  isAFLocalMarketChangesEnabled,
  isAFLocalMarketStepOneOtherLocationLabelEnabled,
}) => {
  const [isHolidayHoursExpanded, setIsHolidayHoursExpanded] = useState(false);
  const { locale, firstDayWeek, timeFormat } = usePageContext();

  const showFeaturesInLocalMarketChanges =
    isAFLocalMarketChangesEnabled &&
    isAFLocalMarketStepOneOtherLocationLabelEnabled;

  const hourOfOperation = useMemo(() => {
    if (option.hoursOfOperation) {
      const schedule = option.hoursOfOperation.filter(
        (element) => element.dayOfWeek !== null,
      ) as Array<OpeningHours>;

      return formatHours(
        schedule,
        true,
        formatPageLocaleToISOCode(locale),
        firstDayWeek,
        timeFormat,
      );
    }

    return [];
  }, [firstDayWeek, locale, option.hoursOfOperation, timeFormat]);

  const holidayHours = useMemo(() => {
    if (option.holidays) {
      return option.holidays.map((holiday) => {
        const date = new Date(holiday.date);
        const formattedDate = new Intl.DateTimeFormat('en-US', {
          month: 'long',
          day: 'numeric',
        }).format(date);

        const formattedSchedule = formatHours(
          [
            {
              dayOfWeek: 0,
              firstStartTime: holiday.firstStartTime ?? -1,
              firstEndTime: holiday.firstEndTime ?? -1,
              secondStartTime: holiday.secondStartTime ?? -1,
              secondEndTime: holiday.secondEndTime ?? -1,
              thirdStartTime: holiday.thirdStartTime ?? -1,
              thirdEndTime: holiday.thirdEndTime ?? -1,
            },
          ],
          true,
          formatPageLocaleToISOCode(locale),
          firstDayWeek,
          timeFormat,
        );

        const status =
          formattedSchedule.length > 0
            ? formattedSchedule[0]?.schedule.join(', ') || 'Closed'
            : 'Closed';

        return {
          scheduleName: holiday.scheduleName || 'Holiday',
          date: formattedDate,
          status,
        };
      });
    }

    return [];
  }, [option]);

  const locationModel = useMemo(
    () => ({
      locationFeatures: {
        is24hoursDropOffAvailable: option?.is24hoursDropOffAvailable ?? false,
        isKeyDropLocation: option?.isKeyDropLocation ?? false,
      },
      address: getLocationDetailsAddress(
        option,
        uiTextData?.multipleLocationsLabel,
      ),
      hourOfOperation,
      holidayHours,
      phones: option.phones ?? [],
    }),
    [option, hourOfOperation, holidayHours],
  );

  return (
    <ConfigItemFormContainerMobile
      testId="location-details-mobile"
      isOpen={isOpen}
      handleClose={handleClose}
      drawerTitle={option.locationName || 'Location Details'}
      drawerIcon={<LocationIcon location={option} mode="detail" />}
    >
      <Box
        data-testid="location-details-mobile-root"
        sx={styles.getRootContainerStyle(!!onSelectLocation)}
      >
        <Stack spacing={2}>
          {/* Address Section */}
          <Box sx={styles.sectionContainerStyle}>
            <AbgTypography
              variant="bodySmallMedium"
              sx={styles.labelStyle}
              data-testid="location-details-mobile-address-label"
            >
              {uiTextData?.addressLabel ?? ''}
            </AbgTypography>
            <Box sx={styles.contentBoxStyle}>
              {locationModel.address.length > 0 ? (
                locationModel.address.map((address, index) => (
                  <AbgTypography
                    key={index}
                    variant={
                      index === 0 ? 'bodySmallMedium' : 'bodySmallRegular'
                    }
                    color="text.secondary"
                    data-testid={`location-details-mobile-address-row-${index}`}
                  >
                    {address}
                  </AbgTypography>
                ))
              ) : (
                <EmptyState />
              )}
            </Box>
          </Box>

          {/* Phone Section */}
          <Box sx={styles.sectionContainerStyle}>
            <AbgTypography
              variant="bodySmallMedium"
              sx={styles.labelStyle}
              data-testid="location-details-mobile-phone-label"
            >
              {uiTextData?.phoneLabel ?? ''}
            </AbgTypography>
            <Box sx={styles.contentBoxStyle}>
              {locationModel.phones?.length ? (
                locationModel.phones.map((phone, index) => (
                  <AbgTypography
                    key={`${phone}-${index}`}
                    variant="bodySmallRegular"
                    color="text.secondary"
                    data-testid={`location-details-mobile-phone-row-${index}`}
                  >
                    {phone}
                  </AbgTypography>
                ))
              ) : (
                <EmptyState />
              )}
            </Box>
          </Box>

          {/* Hours of Operation Section */}
          <Stack spacing={2}>
            <Box sx={styles.dividerContainerStyle}>
              <AbgDivider
                type="horizontal"
                thickness={1}
                color={paletteBase.lowContrastStroke}
              />
            </Box>

            <AbgTypography
              variant="bodySmallMedium"
              data-testid="location-details-mobile-hoursOfOperation-label"
            >
              {uiTextData?.hoursOfOperationLabel ?? ''}
            </AbgTypography>

            <Stack spacing={0.5}>
              {locationModel.hourOfOperation.length > 0 ? (
                locationModel.hourOfOperation.map((entry, dayIndex) => (
                  <Box
                    key={`${entry.days}-${dayIndex}`}
                    sx={styles.hoursGridStyle}
                    data-testid={`location-details-mobile-hoursOfOperation-dayRow-${dayIndex}`}
                  >
                    <AbgTypography
                      variant="bodySmallRegular"
                      color="text.secondary"
                      data-testid={`mobile-hoursOfOperation-dayRow-${dayIndex}-label`}
                    >
                      {entry.days}
                    </AbgTypography>
                    <Box
                      data-testid={`mobile-hoursOfOperation-dayRow-${dayIndex}-values`}
                    >
                      {entry.schedule.map((time, timeIndex) => (
                        <AbgTypography
                          key={`${time}-${timeIndex}`}
                          variant="bodySmallRegular"
                          color="text.secondary"
                          data-testid={`mobile-hoursOfOperation-dayRow-${dayIndex}-timeRow-${timeIndex}`}
                        >
                          {time}
                        </AbgTypography>
                      ))}
                    </Box>
                  </Box>
                ))
              ) : (
                <EmptyState />
              )}
            </Stack>
          </Stack>

          {/* Holiday Hours Section */}
          {holidayHours.length > 0 && (
            <Stack spacing={2}>
              <Box sx={styles.dividerContainerStyle}>
                <AbgDivider
                  type="horizontal"
                  thickness={1}
                  color={paletteBase.lowContrastStroke}
                />
              </Box>

              <LocationHolidayHours
                holidayHours={holidayHours}
                isExpanded={isHolidayHoursExpanded}
                onToggle={setIsHolidayHoursExpanded}
                title={uiTextData?.holidayHoursLabel ?? ''}
              />
            </Stack>
          )}

          {/* Features Section */}
          {(showFeaturesInLocalMarketChanges ||
            locationModel.locationFeatures.is24hoursDropOffAvailable) && (
            <Stack spacing={2}>
              <Box sx={styles.dividerContainerStyle}>
                <AbgDivider
                  type="horizontal"
                  thickness={1}
                  color={paletteBase.lowContrastStroke}
                />
              </Box>
              <AbgTypography
                variant="bodySmallMedium"
                data-testid="location-details-mobile-features-label"
              >
                {uiTextData?.featuresLabel ?? ''}
              </AbgTypography>

              <LocationFeatures
                features={locationModel.locationFeatures}
                featureTranslations={{
                  is24hoursDropOffAvailable:
                    uiTextData?.is24hoursDropOffAvailableLabel ?? '',
                  isKeyDropLocation: uiTextData?.isKeyDropLocationLabel ?? '',
                }}
                dataTestId="location-details-mobile-location-features-container"
                siteCodeGroup={option.siteCodeGroup ?? undefined}
                isAvisFirstLocation={option.isAvisFirstLocation}
                isAvisFirstLiteLocation={option.isAvisFirstLite}
                deliveryLabels={{
                  'avisfirst.lite.delivery.label':
                    uiTextData?.avisFirstLiteDeliveryLabel,
                  'avisfirst.curbside.delivery.label':
                    uiTextData?.avisFirstCurbsideDeliveryLabel ?? '',
                  'avisfirst.athome.delivery.label':
                    uiTextData?.avisFirstAtHomeDeliveryLabel ?? '',
                }}
                locationType={locationType}
              />
            </Stack>
          )}
        </Stack>
        {onSelectLocation && (
          <Box sx={styles.fixedBottomButtonContainerStyle}>
            <Button
              variant="contained"
              fullWidth
              onClick={onSelectLocation}
              data-testid="location-details-mobile-select-button"
              sx={styles.selectButtonStyle}
            >
              {locationType === 'pickup'
                ? (uiTextData?.selectAsPickupLabel ?? '')
                : (uiTextData?.selectAsDropoffLabel ?? '')}
            </Button>
          </Box>
        )}
      </Box>
    </ConfigItemFormContainerMobile>
  );
};
